import React, { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { saveImageToIndexedDB } from "../../storage/imageStorage";
import FoodImage from "../utils/FoodImage";
import { addFood, updateFood } from "../../store/foodsSlice";
import { nanoid } from "@reduxjs/toolkit";
import { parseSpeechNumber } from "../../utils/speechNumber";
import SpeechTextInput from "../utils/SpeechTextInput";

import { colors, space } from "../../design/tokens";
import {
  Card, TextInput, Button, Select, Dialog, Checkbox,
} from "../layout";

export default function FoodDetailModal({ open, food, onClose }) {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [foodData, setFoodData] = useState(getInitialFood());

  function getInitialFood() {
    if (food) return {
      ...food,
      calories: food.calories ?? "",
      carbs: food.carbs ?? "",
      sugar: food.sugar ?? "",
      protein: food.protein ?? "",
      fat: food.fat ?? "",
      gram: food.gram ?? "",
    };
    return {
      id: nanoid(), name: "", image: "", calories: "", carbs: "",
      sugar: "", protein: "", fat: "", gram: "", categorie: "", isRecipe: false,
    };
  }

  useEffect(() => {
    if (open) setFoodData(getInitialFood());
  }, [food, open]);

  // helper for number fields
  const setField = (field) => (val) =>
    setFoodData((prev) => ({ ...prev, [field]: val }));

  const handleSave = () => {
    const normalizedFood = {
      name: foodData.name?.trim() || "Unnamed Food",
      calories: Number(foodData.calories) || 0,
      fat:      Number(foodData.fat)      || 0,
      carbs:    Number(foodData.carbs)    || 0,
      sugar:    Number(foodData.sugar)    || 0,
      protein:  Number(foodData.protein)  || 0,
      gram:     Number(foodData.gram)     || 0,
      imageKey: foodData.imageKey,
      categorie: foodData.categorie?.trim(),
      isRecipe: foodData.isRecipe,
      id: foodData.id,
    };
    food?.id ? dispatch(updateFood(normalizedFood)) : dispatch(addFood(normalizedFood));
    onClose();
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const imageKey = `food_${foodData.id}`;
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.src = url;
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement("canvas");
      canvas.width = 100; canvas.height = 100;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, 100, 100);
      canvas.toBlob(async (blob) => {
        await saveImageToIndexedDB(imageKey, blob);
        setFoodData((prev) => ({ ...prev, imageKey }));
      }, "image/jpeg", 0.7);
    };
  };

  return (
    <Dialog open={open} onClose={onClose} label="Create new food">
      <Card style={{ padding: "0px 16px" }}>

        {foodData.imageKey ? (
          <FoodImage imageKey={foodData.imageKey} alt={foodData.name} />
        ) : (
          <Card style={{ width: "100%", height: 140, display: "flex", alignItems: "center", justifyContent: "center" }}>
            No Image
          </Card>
        )}

        <Button onClick={() => fileInputRef.current.click()} style={{ marginBottom: space.md }}>
          Upload / Take Photo
        </Button>
        {/* <input type="file" accept="image/*" ref={fileInputRef} onChange={handleImageChange} style={{ display: "none" }} /> */}
        <input 
          type="file" 
          accept="image/*" 
          capture="environment"
          // Manche Android-Versionen reagieren besser, wenn man 'multiple' explizit entfernt
          // multiple={false} 
          ref={fileInputRef} 
          onChange={handleImageChange} 
          style={{ display: "none" }} 
        />


        <SpeechTextInput label="Food Name" type="text"
          value={foodData.name}
          onChange={(e) => setField("name")(e.target.value)}
          onSpeechResult={(text) => setField("name")(text)}
          style={{ marginBottom: space.md }}
        />
        {[
          { label: "Calories", field: "calories" },
          { label: "Fat",      field: "fat" },
          { label: "Carbs",    field: "carbs" },
          { label: "Sugar",    field: "sugar" },
          { label: "Protein",  field: "protein" },
        ].map(({ label, field }) => (
          <SpeechTextInput key={field} label={label} type="number"
            value={foodData[field]}
            onChange={(e) => setField(field)(e.target.value)}
            onSpeechResult={(text) => {
              const val = parseSpeechNumber(text);
              if (val !== null) setField(field)(val);
            }}
            style={{ marginBottom: space.md }}
          />
        ))}

        <TextInput label="Weight" type="number"
          value={foodData.gram}
          onChange={(e) => setField("gram")(e.target.value)}
          style={{ marginBottom: space.md }}
        />

        <Select
          value={foodData.categorie}
          placeholder="All categories"
          options={[
            { label: "All categories", value: "all" },
            { label: "Meat", value: "meat" },
            { label: "Fish", value: "fish" },
            { label: "Dairy", value: "dairy" },
            { label: "Vegetable", value: "vegetable" },
            { label: "Fruits", value: "fruits" },
            { label: "Grain", value: "grain" },
            { label: "Side dish", value: "side dish" },
            { label: "Drink", value: "drink" },
            { label: "Sweet", value: "sweet" },
            { label: "Vegan", value: "vegan" },
            { label: "Other", value: "other" },
          ]}
          onChange={(categorie) => setField("categorie")(categorie)}
          style={{ marginBottom: space.md }}
        />

        <Checkbox label="Is Recipe" checked={foodData.isRecipe}
          onChange={(isRecipe) => setField("isRecipe")(isRecipe)}
          style={{ marginBottom: space.md }}
        />

        <Button onClick={handleSave} style={{ marginBottom: space.lg }}>
          Save / Add to Foods
        </Button>
      </Card>
    </Dialog>
  );
}